import axios from 'axios';
import BASE_URL from '../config';

/**
 * Accessible view for the Mavis portal: high-contrast themes and page size, remembered per user
 * (stored on the account so it follows them across devices, cached in this browser for instant
 * start-up before sign-in).
 */
const LOCAL_KEY = 'mavis.a11y';
export const A11Y_THEMES = [
  { id: 'hc-dark', label: 'High contrast: white on black' },
  { id: 'hc-yellow', label: 'High contrast: yellow on black' },
  { id: 'hc-light', label: 'High contrast: black on white' },
];
export const A11Y_DEFAULTS = { enabled: false, theme: 'hc-dark', scale: 130 };
export const A11Y_MIN = 100;
export const A11Y_MAX = 250;

export const loadA11y = () => {
  try {
    return { ...A11Y_DEFAULTS, ...(JSON.parse(localStorage.getItem(LOCAL_KEY)) || {}) };
  } catch (e) {
    return { ...A11Y_DEFAULTS };
  }
};

export const applyA11y = (s) => {
  const root = document.documentElement;
  root.setAttribute('data-a11y', s.enabled ? 'on' : 'off');
  root.setAttribute('data-a11y-theme', s.theme);
  // "zoom" scales text and layout together and reflows like browser zoom, which works for pages
  // whose sizes are set in pixels as well as rem.
  root.style.zoom = s.enabled && s.scale !== 100 ? String(s.scale / 100) : '';
};

const cache = (s, owner) => {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify({ ...s, owner })); } catch (e) { /* ignore */ }
};

// The signed-in user's id, read from the JWT (no network call).
export const currentUserId = () => {
  try {
    const t = localStorage.getItem('jwt_token');
    if (!t) return null;
    const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.user_id != null ? String(payload.user_id) : null;
  } catch (e) {
    return null;
  }
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwt_token')}` } });

const push = async (s) => {
  try {
    await axios.put(`${BASE_URL}/api/accessibility/`, { enabled: s.enabled, theme: s.theme, scale: s.scale }, authHeaders());
  } catch (e) { /* the local copy still applies */ }
};

export const saveA11y = (s) => {
  cache(s, currentUserId());
  applyA11y(s);
  push(s);
};

// Called once the user is signed in: take the account's saved setting, or adopt this browser's
// setting if it was this same user's and nothing is stored yet; otherwise start from "off".
export const syncA11yFromServer = async () => {
  const uid = currentUserId();
  if (!uid) return loadA11y();
  const local = loadA11y();
  try {
    const { data } = await axios.get(`${BASE_URL}/api/accessibility/`, authHeaders());
    if (data.exists) {
      const s = { enabled: data.enabled, theme: data.theme, scale: data.scale };
      cache(s, uid); applyA11y(s);
      return s;
    }
  } catch (e) {
    return local;
  }
  let local2;
  try { local2 = JSON.parse(localStorage.getItem(LOCAL_KEY)) || {}; } catch (err) { local2 = {}; }
  if (local2.owner === uid && local.enabled) { push(local); return local; }
  const off = { ...A11Y_DEFAULTS };
  cache(off, uid); applyA11y(off);
  return off;
};

const labelIconButtons = () => {
  document.querySelectorAll('button:not([aria-label])').forEach((b) => {
    if (!b.textContent.trim() && b.getAttribute('title')) b.setAttribute('aria-label', b.getAttribute('title'));
  });
};

export const initAccessibility = () => {
  applyA11y(loadA11y());
  let queued = false;
  new MutationObserver(() => {
    if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; labelIconButtons(); }); }
  }).observe(document.body, { childList: true, subtree: true });
  labelIconButtons();
};
